import {
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js/max'

import { countryListWithFlags } from '@repo/features/country/data'

export type PhoneFormat = { label: string; value: string }

export interface PhoneState {
  formats: PhoneFormat[]
  validationMessage?: string
}

export function getCountryFromInternationalPhone(value: string): CountryCode | undefined {
  if (!value) return undefined

  const normalized = value.startsWith('00') ? '+' + value.slice(2) : value
  if (!normalized.startsWith('+')) return undefined

  const parsed = parsePhoneNumberFromString(normalized)
  if (parsed) {
    if (parsed.country) {
      const supported = countryListWithFlags.find(c => c.code === parsed.country)
      if (supported) return parsed.country
    }
    const map = getSupportedCallingCodeMap()
    const countries = map.get(parsed.countryCallingCode)
    if (countries && countries.length > 0) {
      return countries[0]
    }
  }

  const digits = normalized.slice(1)
  const map = getSupportedCallingCodeMap()

  for (let len = 3; len >= 1; len--) {
    if (digits.length < len) continue
    const prefix = digits.slice(0, len)
    const countries = map.get(prefix)
    if (countries && countries.length > 0) {
      return countries[0]
    }
  }

  return undefined
}

export function getCountryName(country?: CountryCode): string {
  return countryListWithFlags.find(c => c.code === country)?.name ?? ''
}

export function getPhoneState(value: string, country: CountryCode | undefined): PhoneState {
  if (!value) {
    return { formats: [] }
  }

  try {
    const parsed = country
      ? parsePhoneNumberFromString(value, country)
      : parsePhoneNumberFromString(value)

    if (parsed) {
      if (country && parsed.country && parsed.country !== country) {
        const parsedCountryName = getCountryName(parsed.country)
        const selectedCountryName = getCountryName(country)
        return {
          formats: [],
          validationMessage: `Phone number country is ${parsedCountryName}, not ${selectedCountryName}`,
        }
      }
      if (parsed.isValid()) {
        const candidates = [
          { label: 'E.164', value: parsed.format('E.164') },
          { label: 'International', value: parsed.formatInternational() },
          { label: 'National', value: parsed.formatNational() },
          { label: 'RFC3966', value: parsed.format('RFC3966') },
        ]
        const seen = new Set<string>()
        const formats = candidates.filter(f => {
          if (seen.has(f.value)) return false
          seen.add(f.value)
          return true
        })
        return { formats, validationMessage: undefined }
      }
      const lengthResult = country
        ? validatePhoneNumberLength(value, country)
        : validatePhoneNumberLength(value)
      if (lengthResult) {
        return { formats: [], validationMessage: getValidationMessage(lengthResult) }
      }
      return {
        formats: [],
        validationMessage: country
          ? `Invalid phone number for ${getCountryName(country)}`
          : 'Invalid phone number',
      }
    }

    const lengthResult = country
      ? validatePhoneNumberLength(value, country)
      : validatePhoneNumberLength(value)
    if (lengthResult) {
      return { formats: [], validationMessage: getValidationMessage(lengthResult) }
    }
    return { formats: [], validationMessage: 'Invalid phone number' }
  } catch {
    return { formats: [], validationMessage: 'Invalid phone number' }
  }
}

let supportedCallingCodeMap: Map<string, CountryCode[]> | undefined

function buildSupportedCallingCodeMap(): Map<string, CountryCode[]> {
  const map = new Map<string, CountryCode[]>()
  for (const { code } of countryListWithFlags) {
    const callingCode = getCountryCallingCode(code)
    const existing = map.get(callingCode) ?? []
    existing.push(code)
    map.set(callingCode, existing)
  }
  return map
}

function getSupportedCallingCodeMap(): Map<string, CountryCode[]> {
  if (!supportedCallingCodeMap) {
    supportedCallingCodeMap = buildSupportedCallingCodeMap()
  }
  return supportedCallingCodeMap
}

function getValidationMessage(result: string): string {
  switch (result) {
    case 'INVALID_COUNTRY':
      return 'Invalid country code'
    case 'NOT_A_NUMBER':
      return 'Invalid phone number'
    case 'TOO_LONG':
      return 'Phone number is too long'
    case 'TOO_SHORT':
      return 'Phone number is too short'
    default:
      return 'Invalid phone number'
  }
}
